import type { CSSProperties } from 'react';
import type { BannerPlacementSettings, DisplaySettings } from '@template-project/funnels-contract';

export function extractBannerPlacement(
  displaySettings?: DisplaySettings | null,
): BannerPlacementSettings | null {
  if (!displaySettings || typeof displaySettings !== 'object') return null;
  const record = displaySettings as Record<string, unknown>;
  const nested = record.bannerPlacement;
  const source =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : record;

  const position = typeof source.position === 'string' ? source.position : null;
  const mobileElementId = typeof source.mobileElementId === 'string' ? source.mobileElementId : null;
  const desktopElementId = typeof source.desktopElementId === 'string' ? source.desktopElementId : null;

  if (!position && !mobileElementId && !desktopElementId) return null;

  return {
    position: (position as BannerPlacementSettings['position']) ?? undefined,
    mobileElementId: mobileElementId ?? undefined,
    desktopElementId: desktopElementId ?? undefined,
  };
}

export type ResolveBannerContainerStyleOptions = {
  /**
   * Hosts that already mount the banner in document flow (e.g. CartPage)
   * must not apply fixed edge styles — otherwise the multi-block cart
   * urgency template overlays native cart lines.
   */
  inlineHost?: boolean;
};

export function resolveBannerContainerStyle(
  placement: BannerPlacementSettings | null,
  options?: ResolveBannerContainerStyleOptions,
): CSSProperties | undefined {
  if (options?.inlineHost) return undefined;

  switch (placement?.position) {
    case 'top':
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 30,
        borderRadius: 0,
        margin: 0,
      };
    case 'bottom':
      return {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 30,
        borderRadius: 0,
        margin: 0,
      };
    default:
      return undefined;
  }
}
